import { useEffect, useState, useRef } from "react";
import "../styles/gis/gis.scss";
import MenuTab from "../components/menu_tab";
import GISHeader from "../components/gis/gisheader";
import myData from "../assets/data/data";
import { FaLayerGroup, FaTimes } from "react-icons/fa"; // Layer icon

import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import MultiPolygon from "ol/geom/MultiPolygon";
import MultiLineString from "ol/geom/MultiLineString";
import Polygon from "ol/geom/Polygon";
import { fromLonLat } from "ol/proj";
import { XYZ } from "ol/source";
import { ZoomToExtent, defaults as defaultControls } from "ol/control";
import LoadingWidget from "../components/loading";
import { Style, Circle as CircleStyle, Fill, Stroke } from "ol/style";
import Overlay from "ol/Overlay"; // Import Overlay for popup
import Legend from "../components/gis/legend";

export default function CIS() {
  const [toggleMenu, setToggleMenu] = useState(false);
  const [map, setMap] = useState(null);
  const [basemapSelected, setBasemapSelected] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [basemapsVisible, setBasemapsVisible] = useState(true);
  const [legendCollapsed, setLegendCollapsed] = useState(true);
  const [layers, setLayers] = useState({
    Wards: {
      url: "/api/wardsserved",
      data: [],
      visibility: true,
      attribute: "subcounty",
      filters: { Githunguri: true, "Lari": true },
      colors: { Githunguri: "#ff00ee", "Lari": "#540066" },
    },
    "CIS: Is customer?": {
      url: "/api/latestcis",
      data: [],
      visibility: true,
      attribute: "do you hav",
      filters: { Yes: true, No: true, "N/A":true },
      colors: { Yes: "blue", No: "gray", "N/A": "red" },
    },
  });

  const mapElement = useRef();
  const popupElement = useRef(); // Reference for popup container
  const popupOverlay = useRef(); // Reference for overlay to show the popup

  useEffect(() => {
    const fetchData = async () => {
      const requests = Object.entries(layers).map(([key, layer]) =>
        fetch(layer.url)
          .then((res) => (res.ok ? res.json() : Promise.reject(`Failed: ${key}`)))
      );
  
      const responses = await Promise.allSettled(requests);
  
      const updatedLayers = { ...layers };
      
      Object.keys(layers).forEach((key, i) => {
        if (responses[i].status === "fulfilled") {
          updatedLayers[key].data = responses[i].value.data;
          setIsLoading(false);
        } else {
          console.error(`Error fetching ${key}:`, responses[i].reason);
        }
      });
  
      setLayers(updatedLayers);
    };
  
    fetchData();
  }, []);

  useEffect(() => {
    const basemapLayer = new TileLayer({
      title: "Basemaps",
      // visible: basemapsVisible,
      source: new XYZ({
        url: myData[basemapSelected].url,
        crossOrigin: "Anonymous",
      }),
    });

    const initialMap = new Map({
      target: mapElement.current,
      layers: [basemapLayer],
      view: new View({
        projection: "EPSG:3857",
        center: fromLonLat([36.777382, -1.040343]),
        zoom: 12.5,
        maxZoom: 32,
      }),
      controls: defaultControls().extend([
        new ZoomToExtent({
          extent: [
            ...fromLonLat([36.6970, -1.1335]),
            ...fromLonLat([36.8846, -0.9800]),
          ],
        }),
      ]),
    });

    setMap(initialMap);

    return () => initialMap.setTarget(undefined);
  }, []);

  useEffect(() => {
    if (!map) return;
  
    const basemapLayer = map
      .getLayers()
      .getArray()
      .find((layer) => layer.get("title") === "Basemaps");
  
    if (basemapLayer) {
      // Change Basemap when basemapSelected
      basemapLayer.setSource(
        new XYZ({
          url: myData[basemapSelected].url,
          crossOrigin: "Anonymous",
        })
      );
      // Hide layer when debasemapSelected
      basemapLayer.setVisible(basemapsVisible);
    }
  }, [basemapSelected, basemapsVisible]);

  useEffect(() => {
    if (!map) return;
  
    // Create an overlay to anchor the popup to the map
    popupOverlay.current = new Overlay({
      element: popupElement.current,
      autoPan: true,
      positioning: "bottom-center",
      offset: [0, -15], // Adjust positioning
    });
    map.addOverlay(popupOverlay.current);
  
    // Add click event to display feature data in the popup
    map.on("singleclick", (event) => {
      const feature = map.forEachFeatureAtPixel(event.pixel, (feat) => feat);
      
      if (feature) {
        const properties = feature.getProperties();
        
        // Remove geometry key from properties
        delete properties.geometry;
  
        // Format properties into an HTML table
        const contentHTML = `
          <table class="popup-table">
            ${Object.entries(properties).map(
              ([key, value]) => `<tr><td><strong>${key}:</strong></td><td>${value}</td></tr>`
            ).join("")}
          </table>
        `;
  
        document.getElementById("popup-content").innerHTML = contentHTML;
        popupOverlay.current.setPosition(event.coordinate);
      } else {
        popupOverlay.current.setPosition(undefined); // Hide popup if no feature is clicked
      }
    });
  
  }, [map]);  


  useEffect(() => {
    if (map) {
      Object.entries(layers).forEach(([layerName, layerConfig]) => {
        let vectorLayer = map
          .getLayers()
          .getArray()
          .find((l) => l.get("title") === layerName);
  
        if (!vectorLayer) {
          vectorLayer = new VectorLayer({
            title: layerName,
            source: new VectorSource(),
          });
          map.addLayer(vectorLayer);
        }
  
        const source = vectorLayer.getSource();
        source.clear();     
  
        if (layerConfig.visibility && layerConfig.data) {
          const styleCache = {};
          const attribute = layerConfig.attribute;
        
          const features = layerConfig.data
            .filter((item) => item.geom && layerConfig.filters[item[attribute]])
            .map((item) => {
              
              let feature;
              
              // Handle MultiLineString Geometry
              if (item.geom?.type === "MultiLineString") {
                const coordinates = item.geom?.coordinates[0].map(([lon, lat]) =>
                  fromLonLat([lon, lat])
                );  
                feature = new Feature({
                  geometry: new MultiLineString([coordinates]),
                  status: item[attribute],
                });
        
                if (!styleCache[item[attribute]]) {
                  styleCache[item[attribute]] = new Style({
                    stroke: new Stroke({
                      color: layerConfig.colors[item[attribute]],
                      width: 1,
                    }),
                  });
                }
              }
        
              // Handle Point Geometry
              else if (item.geom.type === "Point") {
                const [lon, lat] = item.geom.coordinates;
                // Create a base properties object
                let properties = {
                  geometry: new Point(fromLonLat([lon, lat])),
                  Name: item["what is yo"],
                  "Connected?": item[attribute],
                  "Buys water?": item["do you cur"],
                  "Captured on": item.date, // Always included
                };

                // Conditionally add attributes
                if (item[attribute] === "Yes") {
                  properties["Account no."] = item["what is _4"];
                  properties["Account name"] = item["what is yo"];
                  properties["Meter Serial"] = item["what is 11"];
                  properties["Meter Condition"] = item["what is th"];
                }

                // Create the feature with the filtered properties
                feature = new Feature(properties);
        
                if (!styleCache[item[attribute]]) {
                  styleCache[item[attribute]] = new Style({
                    image: new CircleStyle({
                      radius: 6,
                      fill: new Fill({ color: layerConfig.colors[item[attribute]] }),
                      stroke: new Stroke({ color: "#fff", width: 1 }),
                    }),
                  });
                }
              }
        
              // Handle MultiPolygon Geometry
              else if (item.geom.type === "MultiPolygon") {
                feature = new Feature({
                  geometry: new MultiPolygon(
                    item.geom.coordinates.map((polygon) =>
                      polygon.map((ring) =>
                        ring.map(([lon, lat]) => fromLonLat([lon, lat]))
                      )
                    )
                  ),
                  Name: item.ward,
                  Subcounty: item[attribute],
                });
        
                if (!styleCache[item[attribute]]) {
                  styleCache[item[attribute]] = new Style({
                    stroke: new Stroke({ color: layerConfig.colors[item[attribute]], width: 0.7 }),
                    fill: new Fill({ color: `${layerConfig.colors[item[attribute]]}09` }), // If colors are hex codes

                  });
                }
              }
        
              if (feature) {
                
                feature.setStyle(styleCache[item[attribute]]);
                return feature;
              }
            });
        
          if (source) {
            source.addFeatures(features.filter(Boolean));
            // if (features.length > 0) {
            //   const extent = source.getExtent();
            //   map.getView().fit(extent, { padding: [30, 200, 80, 50], maxZoom: 18 });
            // }
          }
        }
        
  
        vectorLayer.setVisible(layerConfig.visibility);
      });
    }
  }, [map, layers]);
  

  return (
    <div className="gis">
      <GISHeader toggleMenu={toggleMenu} setToggleMenu={setToggleMenu} />
      {toggleMenu && <MenuTab toggleMenu={toggleMenu} 
                    setToggleMenu={setToggleMenu}
                  />}
      <div className="map">
        {isLoading && <LoadingWidget />}
        <div className="map_container" ref={mapElement}>
          {/* Popup Container */}
          <div ref={popupElement} className="ol-popup">
            <button className="ol-popup-closer" onClick={() => popupOverlay.current.setPosition(undefined)}>
              <FaTimes />
            </button>
            <div id="popup-content"></div>
          </div>
        </div>

        {legendCollapsed ? 
          <button className="legend-toggle custom-icon" onClick={() => setLegendCollapsed(!legendCollapsed)}>
            <FaLayerGroup />
          </button>
        :
        <Legend 
          layers={layers}
          setLayers = {setLayers} 
          myData = {myData} 
          legendCollapsed = {legendCollapsed} 
          setLegendCollapsed = {setLegendCollapsed} 
          basemapsVisible = {basemapsVisible} 
          setBasemapsVisible = {setBasemapsVisible} 
          basemapSelected = {basemapSelected} 
          setBasemapSelected = {setBasemapSelected}
        />
        }
      </div>
    </div>
  );
}